import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { matchesSearch } from '@/lib/search-normalize';
import { customFieldSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { fieldTypeLabel, USER_CUSTOM_FIELD_TYPES } from '@/lib/user-custom-field-types';

interface CustomField {
  id: string;
  name: string;
  slug: string;
  group?: string;
  groupId?: string;
  type?: string;
  description?: string | null;
  helpText?: string | null;
  isActive?: boolean;
}

interface FieldGroup {
  id: string;
  name: string;
}

const FILTER_FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-2.5 px-4 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const PER_PAGE = 10;

function CustomFieldRowActions({ field }: { field: CustomField }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission(PERMISSIONS.CAMPOS_USUARIO_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => api(`/user-custom-fields/${field.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['user-custom-fields'] });
      void queryClient.invalidateQueries({ queryKey: ['user-custom-fields', 'form'] });
      toast(customFieldSuccessToast('delete'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el campo personalizado'));
    },
  });

  return (
    <>
      <div className="flex items-center justify-end gap-1">
        <Link
          to={`/campos-personalizados/${field.id}/edit`}
          className="lms-gradebook__action"
          title="Editar"
          aria-label={`Editar ${field.name}`}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            edit
          </span>
        </Link>
        {canDelete ? (
          <button
            type="button"
            className="lms-gradebook__action"
            title="Eliminar"
            aria-label={`Eliminar ${field.name}`}
            onClick={() => setDeleteOpen(true)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              delete
            </span>
          </button>
        ) : null}
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar campo personalizado"
        description={`¿Está seguro de eliminar «${field.name}»? Se borrarán también los valores guardados en los usuarios.`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

export function UserCustomFieldsPage() {
  const { hasPermission } = usePermissions();
  const canEditFields = hasPermission(PERMISSIONS.CAMPOS_USUARIO_EDITAR);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('active');
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-custom-fields'],
    queryFn: () => listResource<CustomField>('/user-custom-fields', { perPage: 200 }),
  });

  const { data: groupsData } = useQuery({
    queryKey: ['user-custom-field-groups'],
    queryFn: () => api<FieldGroup[]>('/user-custom-fields/groups'),
  });

  const groups = groupsData ?? [];

  const filteredFields = useMemo(() => {
    let rows = data?.data ?? [];
    rows = rows.filter((field) => matchesSearch(`${field.name} ${field.slug}`, search));
    if (group) rows = rows.filter((field) => field.groupId === group);
    if (type) rows = rows.filter((field) => field.type === type);
    if (status === 'active') rows = rows.filter((field) => field.isActive !== false);
    if (status === 'inactive') rows = rows.filter((field) => field.isActive === false);
    return rows;
  }, [data?.data, search, group, type, status]);

  const filteredTotal = filteredFields.length;
  const totalPages = Math.max(1, Math.ceil(filteredTotal / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = filteredTotal === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, filteredTotal);

  const fields = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return filteredFields.slice(start, start + PER_PAGE);
  }, [filteredFields, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [search, group, type, status]);

  function clearFilters() {
    setSearch('');
    setGroup('');
    setType('');
    setStatus('active');
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=users" label="Volver a Administración" />
          <div className="max-w-2xl">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Campos del perfil de usuario</h1>
            <p className="mt-1 text-dash-on-surface-variant">
              Defina campos adicionales para el perfil de usuario sin modificar la base de datos. Administre la
              información extendida de su institución de manera flexible.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 lg:shrink-0 lg:pl-14">
          {canEditFields ? (
            <Link
              to="/campos-personalizados/orden"
              className="inline-flex items-center gap-2 rounded-lg border border-dash-outline-variant bg-white px-5 py-2.5 text-sm font-medium text-dash-on-surface transition-colors hover:bg-secondary-50"
            >
              <span className="material-symbols-outlined text-xl" aria-hidden="true">
                reorder
              </span>
              Formulario
            </Link>
          ) : null}
          <Link
            to="/campos-personalizados/grupos"
            className="inline-flex items-center gap-2 rounded-lg border border-dash-outline-variant bg-white px-5 py-2.5 text-sm font-medium text-dash-on-surface transition-colors hover:bg-secondary-50"
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              folder
            </span>
            Grupos
          </Link>
          <Link
            to="/campos-personalizados/preview"
            className="inline-flex items-center gap-2 rounded-lg border border-dash-primary px-5 py-2.5 text-sm font-medium text-dash-primary transition-colors hover:bg-brand-50"
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              visibility
            </span>
            Vista previa
          </Link>
          <Link
            to="/campos-personalizados/create"
            className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90"
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              add
            </span>
            Nuevo campo
          </Link>
        </div>
      </div>

      <div className="lms-credentials-glass-panel mb-8 rounded-xl border border-dash-outline-variant p-6 shadow-sm">
        <form method="get" className="grid grid-cols-1 items-end gap-4 md:grid-cols-2 lg:grid-cols-12" onSubmit={(event) => event.preventDefault()}>
          <div className="lg:col-span-4">
            <label htmlFor="cf-search" className="mb-2 block text-xs font-semibold text-dash-on-surface-variant">
              Buscar campo
            </label>
            <SearchInput
              id="cf-search"
              name="search"
              value={search}
              onChange={setSearch}
              placeholder="Nombre o slug..."
              aria-label="Buscar campo"
            />
          </div>
          <div className="lg:col-span-2">
            <label htmlFor="cf-group" className="mb-2 block text-xs font-semibold text-dash-on-surface-variant">
              Grupo
            </label>
            <div className="relative">
              <select id="cf-group" name="group" value={group} onChange={(e) => setGroup(e.target.value)} className={`${FILTER_FIELD} cursor-pointer appearance-none pr-10`}>
                <option value="">Todos</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
                <span className="material-symbols-outlined text-xl">expand_more</span>
              </span>
            </div>
          </div>
          <div className="lg:col-span-2">
            <label htmlFor="cf-type" className="mb-2 block text-xs font-semibold text-dash-on-surface-variant">
              Tipo
            </label>
            <div className="relative">
              <select id="cf-type" name="type" value={type} onChange={(e) => setType(e.target.value)} className={`${FILTER_FIELD} cursor-pointer appearance-none pr-10`}>
                <option value="">Todos</option>
                {USER_CUSTOM_FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
                <span className="material-symbols-outlined text-xl">expand_more</span>
              </span>
            </div>
          </div>
          <div className="lg:col-span-2">
            <label htmlFor="cf-status" className="mb-2 block text-xs font-semibold text-dash-on-surface-variant">
              Estado
            </label>
            <div className="relative">
              <select id="cf-status" name="status" value={status} onChange={(e) => setStatus(e.target.value)} className={`${FILTER_FIELD} cursor-pointer appearance-none pr-10`}>
                <option value="all">Todos</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant" aria-hidden="true">
                <span className="material-symbols-outlined text-xl">expand_more</span>
              </span>
            </div>
          </div>
          <div className="flex gap-2 lg:col-span-2">
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant p-2.5 text-dash-on-surface-variant transition-colors hover:bg-secondary-100"
              title="Limpiar"
              aria-label="Limpiar filtros"
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                filter_alt_off
              </span>
            </button>
          </div>
        </form>
      </div>

      {error && (
        <p className="mb-4 text-sm text-error-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : filteredTotal === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    tune
                  </span>
                </div>
                {(data?.data?.length ?? 0) === 0 ? (
                  <>
                    <h3>Sin campos</h3>
                    <p>Cree el primer campo personalizado para usuarios.</p>
                  </>
                ) : (
                  <SearchEmptyState term={search.trim()} onClear={clearFilters} />
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Nombre
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Slug
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Grupo
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Tipo
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Estado
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((field) => (
                      <tr key={field.id}>
                        <th className="lms-gradebook__td" scope="row">
                          <Link to={`/campos-personalizados/${field.id}/edit`} className="lms-gradebook__name">
                            {field.name}
                          </Link>
                          {field.description || field.helpText ? (
                            <div className="lms-gradebook__muted">{field.description || field.helpText}</div>
                          ) : null}
                        </th>
                        <td className="lms-gradebook__td">
                          <span className="lms-gradebook__muted font-mono">{field.slug}</span>
                        </td>
                        <td className="lms-gradebook__td">
                          {field.group ?? <span className="lms-gradebook__muted">—</span>}
                        </td>
                        <td className="lms-gradebook__td">
                          <Badge>{fieldTypeLabel(field.type ?? 'text')}</Badge>
                        </td>
                        <td className="lms-gradebook__td">
                          {field.isActive !== false ? (
                            <Badge variant="success">Activo</Badge>
                          ) : (
                            <Badge variant="warning">Inactivo</Badge>
                          )}
                        </td>
                        <td className="lms-gradebook__td lms-gradebook__td--actions">
                          <CustomFieldRowActions field={field} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de{' '}
                  <strong>{filteredTotal}</strong> resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
